import React, { useState, useCallback } from 'react';
import type {
  NumericFieldFormData,
  NumericFieldFormLabels,
  NumericFieldFormProps,
  FieldTypeConfig,
  UnitOption,
} from '../../shared/types/numericFieldForm';
import { SCORE_COLORS } from './IconPicker';

export type { NumericFieldFormData, NumericFieldFormLabels, NumericFieldFormProps };

export function NumericFieldForm({
  initialData,
  onSubmit,
  onCancel,
  onIconPickerOpen,
  errors = {},
  labels: l,
  mode,
  fieldTypes,
  unitOptions,
  isSubmitting = false,
}: NumericFieldFormProps) {
  const [formData, setFormData] = useState<NumericFieldFormData>(initialData);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleFieldTypeSelect = useCallback((config: FieldTypeConfig) => {
    setFormData(prev => ({
      ...prev,
      fieldType: config.type,
      icon: prev.icon === 'question-mark' ? config.defaultIcon : prev.icon,
      minValue: config.minValue,
      maxValue: config.maxValue,
      stepValue: config.stepValue,
      targetValue: config.targetValue,
      units: config.units,
    }));
  }, []);

  const handleAdvancedChange = useCallback((field: keyof NumericFieldFormData, value: number | string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  const backgroundColor = SCORE_COLORS[formData.score] ?? SCORE_COLORS[100];

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Icon picker trigger + Name input */}
      <div className="form-group row mb-2">
        <div className="col-12">
          <button
            type="button"
            className="btn p-0 me-2 d-inline-block"
            onClick={onIconPickerOpen}
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor,
              border: '2px solid rgba(0,0,0,0.1)',
              cursor: 'pointer',
              verticalAlign: 'top',
            }}
            data-testid="icon-picker-trigger"
          >
            <i
              className={`icon-flex icon-${formData.icon}`}
              style={{ width: 40, height: 40 }}
            />
          </button>

          <div className="form-floating d-inline-block" style={{ width: 'calc(100% - 80px)', verticalAlign: 'top' }}>
            <input
              type="text"
              className="form-control"
              id="numeric-field-name"
              value={formData.name}
              onChange={handleNameChange}
              required
              autoFocus={mode === 'new'}
              placeholder={l.name}
            />
            <label htmlFor="numeric-field-name" className="form-label">{l.name}</label>
          </div>
        </div>
      </div>

      {/* Field type selector */}
      <div className="mb-4">
        <label className="form-label">{l.fieldType}</label>
        <div className="row g-3">
          {fieldTypes.map((config) => (
            <div key={config.type} className="col-md-4 col-6">
              <div
                className={`card h-100 ${formData.fieldType === config.type ? 'border-primary' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleFieldTypeSelect(config)}
                data-testid={`field-type-${config.type}`}
              >
                <div className="card-body text-center">
                  <div className="mb-3">
                    <i className={`icon-flex icon-${config.defaultIcon}`} style={{ width: 48, height: 48 }} />
                  </div>
                  <h6 className="card-title">{config.label}</h6>
                  <p className="card-text text-muted small">{config.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Options Section */}
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-link text-decoration-none p-0"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <i className={`bi bi-chevron-${showAdvanced ? 'down' : 'right'}`}></i>
          {' '}{l.advancedOptions}
        </button>

        {showAdvanced && (
          <div className="card card-body mt-3">
            <div className="row">
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="min-value"
                    value={formData.minValue ?? ''}
                    onChange={(e) => handleAdvancedChange('minValue', parseInt(e.target.value) || 0)}
                    step={1}
                  />
                  <label htmlFor="min-value">{l.minValue}</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="max-value"
                    value={formData.maxValue ?? ''}
                    onChange={(e) => handleAdvancedChange('maxValue', parseInt(e.target.value) || 0)}
                    step={1}
                  />
                  <label htmlFor="max-value">{l.maxValue}</label>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="step-value"
                    value={formData.stepValue ?? ''}
                    onChange={(e) => handleAdvancedChange('stepValue', parseInt(e.target.value) || 1)}
                    step={1}
                  />
                  <label htmlFor="step-value">{l.stepValue}</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating mb-3">
                  <input
                    type="number"
                    className="form-control"
                    id="target-value"
                    value={formData.targetValue ?? ''}
                    onChange={(e) => handleAdvancedChange('targetValue', parseInt(e.target.value) || 0)}
                    step={1}
                  />
                  <label htmlFor="target-value">{l.targetValue}</label>
                </div>
              </div>
            </div>
            <div className="form-floating mb-3">
              <select
                className="form-select"
                id="units"
                value={formData.units || 'none'}
                onChange={(e) => handleAdvancedChange('units', e.target.value)}
              >
                {unitOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label htmlFor="units">{l.units}</label>
            </div>
          </div>
        )}
      </div>

      {/* Form Buttons */}
      <div className="form-group d-flex justify-content-end buttons-wrapper">
        <button
          type="button"
          className="btn btn-link me-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
          data-testid="save-button"
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default NumericFieldForm;
